import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { countries } from '../../../data/countries.data';
import { ICountry } from '../../../models/country.interface';

@Component({
  selector: 'app-country-info',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="flex items-center">
      <!-- Flag is always visible -->
      @if (country && country.options && country.options.icon) {
        <img
          [src]="country.options.icon"
          [alt]="country.name"
          class="h-5 w-7 border border-gray-200"
          [title]="country.name"
        />
      }

      <!-- ISO code (optional - responsive with Tailwind) -->
      @if (showIsoCode && country && country.code) {
        <!-- Mobile view controlled by showIsoCodeOnMobile input -->
        @if (showIsoCodeOnMobile) {
          <span class="ml-2 text-xs font-medium text-gray-600">
            {{ country.code }}
          </span>
        } @else {
          <!-- Hidden on small screens, visible on sm and up -->
          <span class="ml-2 hidden text-xs font-medium text-gray-600 sm:inline-block">
            {{ country.code }}
          </span>
        }
      }

      <!-- Country name (optional - responsive with Tailwind) -->
      @if (showName && country && country.name) {
        <!-- Mobile view controlled by showNameOnMobile input -->
        @if (showNameOnMobile) {
          <span class="ml-2 font-medium">
            {{ country.name }}
          </span>
        } @else {
          <!-- Hidden on small screens, visible on sm and up -->
          <span class="ml-2 hidden font-medium sm:inline-block">
            {{ country.name }}
          </span>
        }
      }

      <!-- Display nothing or placeholder if no country -->
      @if (!country && showPlaceholder) {
        <span class="text-gray-400">-</span>
      }
    </div>
  `
})
export class CountryInfoComponent implements OnChanges {
  /**
   * The ISO country code to display information for
   */
  @Input({ required: true }) countryCode: string = '';

  /**
   * Whether to show the country name (defaults to true)
   */
  @Input() showName: boolean = true;

  /**
   * Whether to show the ISO code (defaults to false)
   */
  @Input() showIsoCode: boolean = false;

  /**
   * Whether to show the flag (defaults to true)
   */
  @Input() showFlag: boolean = true;

  /**
   * Whether to show a placeholder when no country is found
   */
  @Input() showPlaceholder: boolean = true;

  /**
   * Whether to show ISO code on mobile view (defaults to false)
   */
  @Input() showIsoCodeOnMobile: boolean = false;

  /**
   * Whether to show name on mobile view (defaults to false)
   */
  @Input() showNameOnMobile: boolean = false;

  /**
   * The resolved country object from the code
   */
  country: ICountry | undefined;

  constructor() {}

  /**
   * React to changes in country code
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['countryCode']) {
      this.findCountryByCode();
    }
  }

  /**
   * Find the country by ISO code from the countries data
   */
  private findCountryByCode(): void {
    if (!this.countryCode) {
      this.country = undefined;
      return;
    }

    this.country = countries.find((country: ICountry) => country.code === this.countryCode.toUpperCase());
  }
}
